import React, { useMemo, useState, useEffect } from 'react'
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useAnalyticsTracked } from './hooks/useAnalyticsTracked'
import { showTrackedLinkCreateModal, hideAnalyticsModal } from '../../../actions'
import AnalyticsPaywall from '../AnalyticsPaywall/AnalyticsPaywall'
import styles from './AnalyticsTracked.module.css'
import { hasFeature } from '../../../utils/general'
import { F_TRACKED_LINKS } from '../../../constants/plans/constants'
import { trackedFeatures } from 'constants'
import { getTrackedLinksEmailSetting, setTrackedLinksEmailSetting } from '../../../services/tracked-links'

const AnalyticsTracked = ({ domain }) => {
  const productId = useSelector((state) => state.manage.userProfile.productId) || null
  const hasAccess = useMemo(() => {
    return hasFeature(productId, F_TRACKED_LINKS)
  }, [productId])

  const dispatch = useDispatch()
  const analyticsModalPayload = useSelector((state) => state.modals.analyticsModalPayload)
  const { trackedLinks, isLoadingLinks, hasLinks, highlightedLabel, handleUpgrade } = useAnalyticsTracked({
    domain
  })

  const siteDomain = domain?.subdomain || domain
  const [emailsEnabled, setEmailsEnabled] = useState(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    if (!hasAccess || !hasLinks || !siteDomain) return
    let cancelled = false
    setLoadFailed(false)
    getTrackedLinksEmailSetting(siteDomain)
      .then(({ enabled }) => {
        if (!cancelled) setEmailsEnabled(enabled)
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [hasAccess, hasLinks, siteDomain, loadAttempt])

  const handleToggleEmails = async (e) => {
    const isChecked = e.target.checked
    setEmailsEnabled(isChecked)
    setIsUpdating(true)
    try {
      await setTrackedLinksEmailSetting({ siteDomain, enabled: isChecked })
    } catch (error) {
      setEmailsEnabled(!isChecked)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCreateLink = () => {
    dispatch(showTrackedLinkCreateModal({ site: analyticsModalPayload.site }))
    dispatch(hideAnalyticsModal())
  }

  if (isLoadingLinks) {
    return (
      <div className="text-center p-3">
        <Spinner animation="border" variant="primary" />
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <AnalyticsPaywall
        titlePrefix="Upgrade to unlock"
        titleHighlight="Tracked Links"
        gifSrc="/assets/img/gifs/tracked-links.gif"
        onUpgrade={handleUpgrade}
        features={trackedFeatures}
      />
    )
  }

  return (
    <div className="p-2">
      {hasLinks ? (
        <>
          <div className="d-flex justify-content-between mb-3 align-items-center">
            <div className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="email-updates-switch"
                label="Email updates"
                checked={!!emailsEnabled}
                onChange={handleToggleEmails}
                disabled={isUpdating || emailsEnabled === null}
                className="mb-0"
              />
              {isUpdating && <Spinner animation="border" size="sm" className="ms-2 text-primary" />}
              {loadFailed && (
                <Button
                  variant="link"
                  size="sm"
                  className="ms-2 p-0"
                  onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                >
                  Couldn&apos;t load. Retry
                </Button>
              )}
            </div>
            <Button variant="primary" onClick={handleCreateLink}>
              Modify links
            </Button>
          </div>

          <Row className="fw-bold d-none d-md-flex pb-2">
            <Col md={2}>Label</Col>
            <Col md={6}>Link</Col>
            <Col md={2} className="text-end">
              Views
            </Col>
            <Col md={2} className="text-end">
              Unique visitors
            </Col>
          </Row>

          <div className={styles.trackedLinksContainer}>
            <div className={styles.trackedLinksList}>
              {trackedLinks.map((link) => (
                <Row
                  key={link.label}
                  className={`align-items-center border mb-2 mb-md-0 py-2 ${
                    highlightedLabel === link.label ? 'bg-light' : ''
                  }`}
                >
                  <Col xs={12} md={2} className="mb-2 mb-md-0 fw-medium text-truncate">
                    {link.label}
                  </Col>
                  <Col xs={12} md={6} className="mb-2 mb-md-0">
                    {link.url}
                  </Col>
                  <Col xs={6} md={2} className="text-md-end">
                    <span className="d-inline d-md-none text-muted small me-1">Views:</span>
                    {link.pageviews}
                  </Col>
                  <Col xs={6} md={2} className="text-md-end">
                    <span className="d-inline d-md-none text-muted small me-1">Unique:</span>
                    {link.visitors}
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="text-center p-3 my-3 bg-light rounded">
          <h5 className="mb-2 bold">No tracked links</h5>
          <p className="text-muted mb-3 mx-auto" style={{ maxWidth: '400px' }}>
            Create unique links for a specific recipient to track when it’s opened
          </p>
          <Button variant="primary" onClick={handleCreateLink}>
            Create link
          </Button>
        </div>
      )}
    </div>
  )
}

export default AnalyticsTracked
